import React, {
  ComponentType,
  CSSProperties,
  forwardRef,
  Fragment,
} from 'react';
import * as S from './styles';
import { IconProps } from '@/types/icon';
import { DefaultTheme, useTheme } from 'styled-components';
import Spinner from '../Spinner';
import { colorSemanticsArray } from '@/styles/theme';

export type ButtonVariant = 'solid' | 'soft' | 'surface' | 'outline' | 'ghost';
export type ButtonColor =
  | 'accent'
  | 'neutral'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';

export type ButtonProps = {
  $size: number;
  $variant: ButtonVariant;
  $color: ButtonColor;
  $highContrast: boolean;
  $loading: boolean;
  $IconStart?: ComponentType<IconProps>;
  $iconStartProps?: object;
  $IconEnd?: ComponentType<IconProps>;
  $iconEndProps?: object;
  $focusRing: boolean;
  $text: string;
  $disable?: boolean;
  style?: CSSProperties;
  $onClick?: () => void;
  $active?: boolean;
  $type?: 'button' | 'reset' | 'submit' | undefined;
  id?: string;
};

export function getCorTexto({
  theme,
  $variant,
  $color,
  $highContrast,
}: {
  theme: DefaultTheme;
  $variant: ButtonVariant;
  $highContrast: boolean;
  $color: string;
}) {
  if (['ghost', 'soft', 'outline'].includes($variant)) {
    if (colorSemanticsArray.includes($color))
      return theme.colors.semantic[$color]['9'];

    return theme.colors[$color][$highContrast ? $color : `${$color}_alpha`][
      $highContrast ? '12' : '11'
    ];
  }
  if (colorSemanticsArray.includes($color)) return theme.tokens.colors.text;

  if ($variant === 'solid' && $color === 'neutral')
    return theme.tokens.colors.white_contrast;

  const constrast = $highContrast ? 'surface' : 'contrast';
  return theme.tokens.colors[`${$color.toLowerCase()}_${constrast}`];
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    style,
    $IconStart,
    $iconStartProps,
    $iconEndProps,
    $IconEnd,
    $text,
    $size,
    $color,
    $variant,
    $highContrast,
    $onClick,
    $disable,
    $type = 'button',
    $loading,
  } = props;
  const theme = useTheme();

  return (
    <S.Container
      disabled={$disable || $loading}
      onClick={$onClick}
      style={style}
      ref={ref}
      type={$type}
      {...props}
    >
      {$loading ? (
        <Spinner
          corPrincipal={theme.colors.neutral.neutral[7]}
          tamanho={theme.typography.font.size[$size + 3]}
        />
      ) : (
        <Fragment>
          {$IconStart ? (
            <$IconStart
              color={getCorTexto({ $color, $variant, theme, $highContrast })}
              size={theme.typography.font.size[$size + 1]}
              {...$iconStartProps}
            />
          ) : null}
          {$text}
          {$IconEnd ? (
            <$IconEnd
              color={getCorTexto({ $color, $variant, theme, $highContrast })}
              size={theme.typography.font.size[$size + 1]}
              {...$iconEndProps}
            />
          ) : null}
        </Fragment>
      )}
    </S.Container>
  );
});

Button.displayName = 'Button';

export default Button;
